import { useEffect, useRef, useState } from 'react'

import { forceAppUpdate } from '../pwa'

/** sessionStorage (per-tab, survives the recovery reload) — one automatic
 *  attempt per tab session, so a failed update can't reload-loop. */
const FLAG = 'polf-stale-recovery-attempted'

export type RecoveryState = 'idle' | 'updating' | 'failed'

/**
 * Self-heal for a shell whose own query the server rejects (stale precached
 * bundle after a schema-breaking deploy — see design.md §8.5). On the first
 * stale detection in a tab session it force-updates the service worker and
 * reloads; if this page load IS the failed attempt, it reports `'failed'` so
 * the shell can offer a manual update button instead of trying again. A
 * healthy render clears the flag, re-arming recovery for a future deploy.
 */
export const useStaleBundleRecovery = (
  stale: boolean,
  healthy: boolean
): RecoveryState => {
  const [state, setState] = useState<RecoveryState>('idle')
  // Also guards in-memory (StrictMode double-effect, storage-blocked private
  // mode). In the worst storage-blocked case each page load retries once,
  // still loop-safe because the fallback reload is timer-gated.
  const attemptedThisLoad = useRef(false)

  useEffect(() => {
    if (healthy) {
      try {
        sessionStorage.removeItem(FLAG)
      } catch {
        // Storage blocked — nothing to clear.
      }
      setState('idle')
      return
    }
    if (!stale || attemptedThisLoad.current) return
    attemptedThisLoad.current = true
    let alreadyAttempted = false
    try {
      alreadyAttempted = sessionStorage.getItem(FLAG) === '1'
      sessionStorage.setItem(FLAG, '1')
    } catch {
      // Storage blocked — rely on attemptedThisLoad alone.
    }
    if (alreadyAttempted) {
      setState('failed')
      return
    }
    setState('updating')
    forceAppUpdate()
  }, [stale, healthy])

  return state
}
